import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../lib/apiClient';
import { changelog } from '../lib/changelog';

export interface FeedbackUpdateNotification {
  id: string;
  type: 'bug' | 'feature';
  title: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  admin_notes: string;
  updated_at: string;
  resolved_at: string | null;
  fixed_in_version: string | null;
}

const POLL_INTERVAL_MS = 60_000;

/**
 * Compare two `vX.Y.Z` (or `X.Y.Z`) version strings. Returns -1, 0, or 1.
 * Anything non-numeric beyond major/minor/patch is ignored — semver-light.
 */
function compareVersions(a: string, b: string): number {
  const parse = (s: string) => s.replace(/^v/, '').split('.').map((p) => parseInt(p, 10) || 0);
  const [a0 = 0, a1 = 0, a2 = 0] = parse(a);
  const [b0 = 0, b1 = 0, b2 = 0] = parse(b);
  if (a0 !== b0) return a0 < b0 ? -1 : 1;
  if (a1 !== b1) return a1 < b1 ? -1 : 1;
  if (a2 !== b2) return a2 < b2 ? -1 : 1;
  return 0;
}

/**
 * The version this browser is currently running. Read from the bundled
 * changelog (which the changelog rule keeps in lockstep with package.json).
 * If a notification's `fixed_in_version` is *higher* than this, the user
 * hasn't loaded the build that contains the fix yet — suppress the notice
 * until they do.
 */
const CLIENT_VERSION = changelog[0]?.version ?? 'v0.0.0';

/**
 * Drop notifications whose fix shipped in a version newer than the build the
 * user has loaded. `null` fixed_in_version means "not a release-shipped fix"
 * (admin note, in-progress status change) — always pass those through.
 */
function passesVersionGate(n: FeedbackUpdateNotification): boolean {
  if (!n.fixed_in_version) return true;
  return compareVersions(CLIENT_VERSION, n.fixed_in_version) >= 0;
}

export function useFeedbackNotifications(enabled: boolean) {
  const [notifications, setNotifications] = useState<FeedbackUpdateNotification[]>([]);
  const [loading, setLoading] = useState(false);
  // Track ids the user has acknowledged in this session so the modal doesn't
  // re-fire between server poll cycles before mark-viewed lands.
  const acknowledgedRef = useRef<Set<string>>(new Set());

  const fetchNow = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const rows = await apiClient.get<FeedbackUpdateNotification[]>('/api/feedback/my-updates');
      const filtered = rows.filter(
        (r) => !acknowledgedRef.current.has(r.id) && passesVersionGate(r),
      );
      setNotifications(filtered);
    } catch (err) {
      // Don't notify(): notifications are passive — a transient fetch error
      // shouldn't pop a dialog.
      console.error('Failed to load feedback notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    fetchNow();
    const interval = window.setInterval(fetchNow, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, fetchNow]);

  const markViewed = useCallback(async (id: string) => {
    acknowledgedRef.current.add(id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      await apiClient.post(`/api/feedback/${id}/mark-viewed`, {});
    } catch (err) {
      // Even if the server call fails, leave the local optimistic state in
      // place — the next poll will re-include it if the server-side mark
      // didn't land, and the user can dismiss again.
      console.error('Failed to mark feedback viewed:', err);
    }
  }, []);

  // Clear the whole list in one action. Optimistically empties the UI, then
  // fires a mark-viewed for each. Individual failures are swallowed (same
  // rationale as markViewed) — the next poll reconciles anything that didn't
  // land server-side.
  const markAllViewed = useCallback(async () => {
    const ids = notifications.map((n) => n.id);
    if (ids.length === 0) return;
    ids.forEach((id) => acknowledgedRef.current.add(id));
    setNotifications([]);
    await Promise.all(
      ids.map((id) =>
        apiClient.post(`/api/feedback/${id}/mark-viewed`, {}).catch((err) => {
          console.error('Failed to mark feedback viewed:', err);
        }),
      ),
    );
  }, [notifications]);

  return { notifications, loading, refresh: fetchNow, markViewed, markAllViewed };
}
